// src/components/admin/PackageForm.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  InputAdornment,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import moment from 'moment';

const PackageForm = ({ initialData, onSave, onCancel }) => {
  // États pour le formulaire
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    price: initialData?.price || '',
    days: initialData?.days || 15,
    availableSpots: initialData?.availableSpots || '',
    startDate: initialData?.startDate ? moment(initialData.startDate) : moment(),
    endDate: initialData?.endDate ? moment(initialData.endDate) : moment().add(15, 'days'),
    departureCity: initialData?.departureCity || '',
    category: initialData?.category || '',
    hotelsMecca: initialData?.hotelsMecca ? [...initialData.hotelsMecca] : [{
      name: '',
      zone: 'A',
      checkIn: moment(),
      checkOut: moment().add(7, 'days'),
      image: null,
      imagePreview: initialData?.hotelMecca?.image || ''
    }],
    hotelsMecca: [
      {
        name: initialData?.hotelMecca?.name || '',
        zone: initialData?.hotelMecca?.zone || 'A',
        checkIn: initialData?.hotelMecca?.checkIn ? moment(initialData.hotelMecca.checkIn) : moment(),
        checkOut: initialData?.hotelMecca?.checkOut ? moment(initialData.hotelMecca.checkOut) : moment().add(7, 'days'),
        image: null,
        imagePreview: initialData?.hotelMecca?.image || ''
      },
      initialData?.hotelMecca2 ? {
        name: initialData.hotelMecca2.name || '',
        zone: initialData.hotelMecca2.zone || 'A',
        checkIn: initialData.hotelMecca2.checkIn ? moment(initialData.hotelMecca2.checkIn) : moment().add(7, 'days'),
        checkOut: initialData.hotelMecca2.checkOut ? moment(initialData.hotelMecca2.checkOut) : moment().add(14, 'days'),
        image: null,
        imagePreview: initialData?.hotelMecca2?.image || ''
      } : null
    ].filter(Boolean),
    
    hotelMedina: {
      name: initialData?.hotelMedina?.name || '',
      checkIn: initialData?.hotelMedina?.checkIn ? moment(initialData.hotelMedina.checkIn) : moment().add(7, 'days'),
      checkOut: initialData?.hotelMedina?.checkOut ? moment(initialData.hotelMedina.checkOut) : moment().add(14, 'days'),
      image: null,
      imagePreview: initialData?.hotelMedina?.image || ''
    },
    minaCamp: initialData?.minaCamp || '',
    flights: {
      outbound: {
        date: initialData?.flights?.outbound?.date ? moment(initialData.flights.outbound.date) : moment(),
        airline: initialData?.flights?.outbound?.airline || '',
      },
      inbound: {
        date: initialData?.flights?.inbound?.date ? moment(initialData.flights.inbound.date) : moment().add(15, 'days'),
        airline: initialData?.flights?.inbound?.airline || '',
      }
    },
    status: initialData?.status || 'draft',
  });

  // État pour l'image principale
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(initialData?.image || '');
  
  // État pour les erreurs
  const [errors, setErrors] = useState({});
  
  // Villes de départ (à récupérer depuis l'API dans une version future)
  const cities = [
    { id: 'paris', name: 'Paris' },
    { id: 'lyon', name: 'Lyon' },
    { id: 'marseille', name: 'Marseille' },
    { id: 'lille', name: 'Lille' },
    { id: 'toulouse', name: 'Toulouse' },
    { id: 'nice', name: 'Nice' },
    { id: 'bordeaux', name: 'Bordeaux' },
  ];

  // Gérer les changements de champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Supprimer l'erreur si le champ est modifié
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Gérer les changements de date
  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Gérer les changements dans les hôtels de La Mecque
  const handleHotelMeccaChange = (index, field, value) => {
    const updatedHotels = [...formData.hotelsMecca];
    updatedHotels[index] = {
      ...updatedHotels[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      hotelsMecca: updatedHotels
    }));
  };
  
  // Ajouter un nouvel hôtel à La Mecque
  const handleAddHotelMecca = () => {
    setFormData(prev => ({
      ...prev,
      hotelsMecca: [
        ...prev.hotelsMecca,
        {
          name: '',
          zone: 'A',
          checkIn: moment(),
          checkOut: moment().add(7, 'days'),
          image: null
        }
      ]
    }));
  };
  
  // Supprimer un hôtel à La Mecque
  const handleRemoveHotelMecca = (index) => {
    if (formData.hotelsMecca.length > 1) {
      const updatedHotels = [...formData.hotelsMecca];
      updatedHotels.splice(index, 1);
      
      setFormData(prev => ({
        ...prev,
        hotelsMecca: updatedHotels
      }));
    }
  };
  
  // Gérer les changements dans l'hôtel de Médine
  const handleHotelMedinaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      hotelMedina: {
        ...prev.hotelMedina,
        [field]: value
      }
    }));
  };
  
  // Gérer les changements dans les vols
  const handleFlightChange = (direction, field, value) => {
    setFormData(prev => ({
      ...prev,
      flights: {
        ...prev.flights,
        [direction]: {
          ...prev.flights[direction],
          [field]: value
        }
      }
    }));
  };
  
  // Gérer l'upload de l'image principale
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImage(file);
      const previewUrl = URL.createObjectURL(file);
      setMainImagePreview(previewUrl);
    }
  };
  
  // Gérer l'upload des images d'hôtels
  const handleHotelImageChange = (e, type, index = 0) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'mecca') {
        const updatedHotels = [...formData.hotelsMecca];
        updatedHotels[index] = {
          ...updatedHotels[index],
          image: file,
          imagePreview: URL.createObjectURL(file)
        };
        
        setFormData(prev => ({
          ...prev,
          hotelsMecca: updatedHotels
        }));
      } else if (type === 'medina') {
        setFormData(prev => ({
          ...prev,
          hotelMedina: {
            ...prev.hotelMedina,
            image: file,
            imagePreview: URL.createObjectURL(file)
          }
        }));
      }
    }
  };
  
  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title) newErrors.title = 'Le titre est requis';
    if (!formData.description) newErrors.description = 'La description est requise';
    if (!formData.price) newErrors.price = 'Le prix est requis';
    else if (isNaN(formData.price) || Number(formData.price) <= 0) newErrors.price = 'Le prix doit être un nombre positif';
    
    if (!formData.days) newErrors.days = 'La durée est requise';
    else if (formData.days < 10 || formData.days > 30) newErrors.days = 'La durée doit être entre 10 et 30 jours';
    
    if (!formData.departureCity) newErrors.departureCity = 'La ville de départ est requise';
    if (!formData.category) newErrors.category = 'La catégorie est requise';
    if (!formData.minaCamp) newErrors.minaCamp = 'Le camp à Mina est requis';
    
    // Vérifier au moins un hôtel à La Mecque
    if (formData.hotelsMecca.length === 0 || !formData.hotelsMecca[0].name) {
      newErrors.hotelMecca = 'Au moins un hôtel à La Mecque est requis';
    }
    
    // Vérifier l'hôtel à Médine
    if (!formData.hotelMedina.name) {
      newErrors.hotelMedina = 'L\'hôtel à Médine est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Créer un objet FormData pour pouvoir envoyer des fichiers
      const formDataToSend = new FormData();
      
      // Ajouter les champs de base
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('days', formData.days);
      formDataToSend.append('availableSpots', formData.availableSpots);
      formDataToSend.append('startDate', formData.startDate.toISOString());
      formDataToSend.append('endDate', formData.endDate.toISOString());
      formDataToSend.append('departureCity', formData.departureCity);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('minaCamp', formData.minaCamp);
      
          // Ajout des logs de débogage
    console.log('Images avant envoi:');
    console.log('- Image principale:', mainImage instanceof File ? 'File valide' : mainImage);
    console.log('- Image Mecque:', formData.hotelsMecca[0].image instanceof File ? 'File valide' : formData.hotelsMecca[0].image);
    console.log('- Image Médine:', formData.hotelMedina.image instanceof File ? 'File valide' : formData.hotelMedina.image);

      // Ajouter l'image principale si elle existe
      if (mainImage) {
        formDataToSend.append('image', mainImage);
      }
      
      // Ajouter les informations du premier hôtel à La Mecque
      formDataToSend.append('hotelMecca.name', formData.hotelsMecca[0].name);
      formDataToSend.append('hotelMecca.zone', formData.hotelsMecca[0].zone);
      formDataToSend.append('hotelMecca.checkIn', formData.hotelsMecca[0].checkIn.toISOString());
      formDataToSend.append('hotelMecca.checkOut', formData.hotelsMecca[0].checkOut.toISOString());
      
      // Ajouter l'image de l'hôtel à La Mecque si elle existe
      if (formData.hotelsMecca[0].image) {
        formDataToSend.append('hotelMeccaImage', formData.hotelsMecca[0].image);
      }
      
      // Ajouter les informations du deuxième hôtel à La Mecque s'il existe
      if (formData.hotelsMecca.length > 1) {
        formDataToSend.append('hotelMecca2.name', formData.hotelsMecca[1].name);
        formDataToSend.append('hotelMecca2.zone', formData.hotelsMecca[1].zone);
        formDataToSend.append('hotelMecca2.checkIn', formData.hotelsMecca[1].checkIn.toISOString());
        formDataToSend.append('hotelMecca2.checkOut', formData.hotelsMecca[1].checkOut.toISOString());
        
        // Ajouter l'image du deuxième hôtel à La Mecque si elle existe
        if (formData.hotelsMecca[1].image) {
          formDataToSend.append('hotelMecca2Image', formData.hotelsMecca[1].image);
        }
      }
      
      // Ajouter les informations de l'hôtel à Médine
      formDataToSend.append('hotelMedina.name', formData.hotelMedina.name);
      formDataToSend.append('hotelMedina.checkIn', formData.hotelMedina.checkIn.toISOString());
      formDataToSend.append('hotelMedina.checkOut', formData.hotelMedina.checkOut.toISOString());
      
      // Ajouter l'image de l'hôtel à Médine si elle existe
      if (formData.hotelMedina.image) {
        formDataToSend.append('hotelMedinaImage', formData.hotelMedina.image);
      }
      
      // Ajouter les informations des vols
      formDataToSend.append('flights.outbound.date', formData.flights.outbound.date.toISOString());
      formDataToSend.append('flights.outbound.airline', formData.flights.outbound.airline);
      formDataToSend.append('flights.inbound.date', formData.flights.inbound.date.toISOString());
      formDataToSend.append('flights.inbound.airline', formData.flights.inbound.airline);
      
      // Envoyer les données
      onSave(formDataToSend);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Informations générales
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="title"
                label="Titre du forfait"
                value={formData.title}
                onChange={handleChange}
                error={Boolean(errors.title)}
                helperText={errors.title}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
                error={Boolean(errors.description)}
                helperText={errors.description}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="price"
                label="Prix (€)"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                value={formData.price}
                onChange={handleChange}
                error={Boolean(errors.price)}
                helperText={errors.price}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="days"
                label="Nombre de jours"
                type="number"
                inputProps={{ min: 10, max: 30 }}
                value={formData.days}
                onChange={handleChange}
                error={Boolean(errors.days)}
                helperText={errors.days}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="availableSpots"
                label="Places disponibles (optionnel)"
                type="number"
                value={formData.availableSpots}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth error={Boolean(errors.status)}>
                <InputLabel>Statut</InputLabel>
                <Select
                  name="status"
                  label="Statut"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <MenuItem value="draft">Brouillon</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="inactive">Inactif</MenuItem>
                </Select>
                {errors.status && (
                  <FormHelperText>{errors.status}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date de début"
                value={formData.startDate}
                onChange={(date) => handleDateChange('startDate', date)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(errors.startDate)}
                    helperText={errors.startDate}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date de fin"
                value={formData.endDate}
                onChange={(date) => handleDateChange('endDate', date)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={Boolean(errors.endDate)}
                    helperText={errors.endDate}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={Boolean(errors.departureCity)}
              >
                <InputLabel>Ville de départ</InputLabel>
                <Select
                  name="departureCity"
                  label="Ville de départ"
                  value={formData.departureCity}
                  onChange={handleChange}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.departureCity && (
                  <FormHelperText>{errors.departureCity}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={Boolean(errors.category)}>
                <InputLabel>Gamme</InputLabel>
                <Select
                  name="category"
                  label="Gamme"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <MenuItem value="standard_non_shifting">Standard non shifting</MenuItem>
                  <MenuItem value="standard_shifting">Standard shifting</MenuItem>
                  <MenuItem value="premium_non_shifting">Premium non shifting</MenuItem>
                  <MenuItem value="premium_shifting">Premium shifting</MenuItem>
                  <MenuItem value="luxury_non_shifting">Luxury non shifting</MenuItem>
                  <MenuItem value="luxury_shifting">Luxury shifting</MenuItem>
                </Select>
                {errors.category && (
                  <FormHelperText>{errors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2, mt: 1 }}>
                <Typography variant="subtitle1">Image principale</Typography>
                {mainImagePreview ? (
                  <Box sx={{ position: 'relative', width: 'fit-content' }}>
                    <CardMedia
                      component="img"
                      image={mainImagePreview}
                      alt="Image du forfait"
                      sx={{ width: 300, height: 180, borderRadius: 1, mt: 1 }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        backgroundColor: 'rgba(121, 87, 87, 0.5)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                      }}
                      onClick={() => {
                        setMainImage(null);
                        setMainImagePreview('');
                      }}
                    >
                      <DeleteIcon sx={{ color: 'white' }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    sx={{ mt: 1 }}
                  >
                    Ajouter une image
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleMainImageChange}
                    />
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Section Hébergement */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Hébergement
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {/* Hôtels à La Mecque */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Hôtels à La Mecque
              </Typography>
              
              {formData.hotelsMecca.map((hotel, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardHeader 
                    title={`Hôtel à La Mecque ${index + 1}`} 
                    action={
                      formData.hotelsMecca.length > 1 && (
                        <IconButton onClick={() => handleRemoveHotelMecca(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Nom de l'hôtel"
                          value={hotel.name}
                          onChange={(e) => handleHotelMeccaChange(index, 'name', e.target.value)}
                          error={index === 0 && Boolean(errors.hotelMecca)}
                          helperText={index === 0 && errors.hotelMecca}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Zone</InputLabel>
                          <Select
                            label="Zone"
                            value={hotel.zone}
                            onChange={(e) => handleHotelMeccaChange(index, 'zone', e.target.value)}
                          >
                            <MenuItem value="A">Zone A</MenuItem>
                            <MenuItem value="B">Zone B</MenuItem>
                            <MenuItem value="C">Zone C</MenuItem>
                            <MenuItem value="M">Zone M</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Date d'arrivée"
                          value={hotel.checkIn}
                          onChange={(date) => handleHotelMeccaChange(index, 'checkIn', date)}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Date de départ"
                          value={hotel.checkOut}
                          onChange={(date) => handleHotelMeccaChange(index, 'checkOut', date)}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">Image de l'hôtel (optionnel)</Typography>
                          {hotel.imagePreview ? (
                            <Box sx={{ position: 'relative', width: 'fit-content' }}>
                              <CardMedia
                                component="img"
                                image={hotel.imagePreview}
                                alt="Image de l'hôtel"
                                sx={{ width: 200, height: 120, borderRadius: 1, mt: 1 }}
                              />
                              <IconButton
                                sx={{
                                  position: 'absolute',
                                  top: 5,
                                  right: 5,
                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  },
                                  padding: '4px',
                                }}
                                size="small"
                                onClick={() => {
                                  const updatedHotels = [...formData.hotelsMecca];
                                  updatedHotels[index] = {
                                    ...updatedHotels[index],
                                    image: null,
                                    imagePreview: null
                                  };
                                  
                                  setFormData(prev => ({
                                    ...prev,
                                    hotelsMecca: updatedHotels
                                  }));
                                }}
                              >
                                <DeleteIcon sx={{ color: 'white', fontSize: '1rem' }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<AddPhotoAlternateIcon />}
                              size="small"
                              sx={{ mt: 1 }}
                            >
                              Ajouter
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => handleHotelImageChange(e, 'mecca', index)}
                              />
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddHotelMecca}
                sx={{ mt: 1 }}
              >
                Ajouter un hôtel à La Mecque
              </Button>
            </Grid>

            {/* Hôtel à Médine */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Hôtel à Médine" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nom de l'hôtel"
                        value={formData.hotelMedina.name}
                        onChange={(e) => handleHotelMedinaChange('name', e.target.value)}
                        error={Boolean(errors.hotelMedina)}
                        helperText={errors.hotelMedina}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Date d'arrivée"
                        value={formData.hotelMedina.checkIn}
                        onChange={(date) => handleHotelMedinaChange('checkIn', date)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Date de départ"
                        value={formData.hotelMedina.checkOut}
                        onChange={(date) => handleHotelMedinaChange('checkOut', date)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Image de l'hôtel (optionnel)</Typography>
                        {formData.hotelMedina.imagePreview ? (
                          <Box sx={{ position: 'relative', width: 'fit-content' }}>
                            <CardMedia
                              component="img"
                              image={formData.hotelMedina.imagePreview}
                              alt="Image de l'hôtel"
                              sx={{ width: 200, height: 120, borderRadius: 1, mt: 1 }}
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 5,
                                right: 5,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                                padding: '4px',
                              }}
                              size="small"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  hotelMedina: {
                                    ...prev.hotelMedina,
                                    image: null,
                                    imagePreview: null
                                  }
                                }));
                              }}
                            >
                              <DeleteIcon sx={{ color: 'white', fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<AddPhotoAlternateIcon />}
                            size="small"
                            sx={{ mt: 1 }}
                          >
                            Ajouter
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={(e) => handleHotelImageChange(e, 'medina')}
                            />
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Camp à Mina */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Camp à Mina" />
                <CardContent>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.minaCamp)}
                  >
                    <InputLabel>Sélection du camp</InputLabel>
                    <Select
                      name="minaCamp"
                      label="Sélection du camp"
                      value={formData.minaCamp}
                      onChange={handleChange}
                    >
                      <MenuItem value="AlMuaisim">AlMuaisim</MenuItem>
                      <MenuItem value="Alkabesh">Alkabesh</MenuItem>
                    </Select>
                    {errors.minaCamp && (
                      <FormHelperText>{errors.minaCamp}</FormHelperText>
                    )}
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Section Vols (optionnelle) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Vols (optionnel)
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {/* Vol aller */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Vol Aller" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Date du vol"
                        value={formData.flights.outbound.date}
                        onChange={(date) => handleFlightChange('outbound', 'date', date)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Compagnie aérienne"
                        value={formData.flights.outbound.airline}
                        onChange={(e) => handleFlightChange('outbound', 'airline', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Vol retour */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Vol Retour" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Date du vol"
                        value={formData.flights.inbound.date}
                        onChange={(date) => handleFlightChange('inbound', 'date', date)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Compagnie aérienne"
                        value={formData.flights.inbound.airline}
                        onChange={(e) => handleFlightChange('inbound', 'airline', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mr: 2 }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            {initialData ? 'Mettre à jour' : 'Créer'}
            </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default PackageForm;